import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Grid,
  Stack,
  Typography,
  TextField,
  Divider,
  MenuItem,
  IconButton,
  Dialog,
  Box,
  CircularProgress,
  Button,
  Paper,
} from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import HomeIcon from "@mui/icons-material/Home";
import DescriptionIcon from "@mui/icons-material/Description";
import { useForm, Controller } from "react-hook-form";
import { UserDetailsContext } from "../../Context/userDetailsContext";
import { useLocation, useNavigate } from "react-router-dom";
import axiosInstance from "../../axiosConfig";
import PreviewAttachmentDialog from "../PreviewAttachmentDialog/index";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { LoadingButton } from "@mui/lab";
import { isMobile } from "react-device-detect";
import ExpandCircleDownIcon from "@mui/icons-material/ExpandCircleDown";
import MessageModal from "../MessageModal";
import CancelIcon from "@mui/icons-material/Cancel";

import GeneralComponent from "./GeneralComponent";
import CertificateComponent from "./CertificateComponent";
import ElectricityComponent from "./ElectricityComponent";
import WaterComponent from "./WaterComponent";
import CraftmentForm from "./CraftmentForm";
import ClearanceCertificateForm from "./ClearanceCertificateForm";
import ChooseLocationFromMapForm from "./ChooseLocationFromMapForm";
import ServiceTypeForm from "./ServiceTypeForm";
import ServiceWithDetailsComponent from "./ServiceWithDetailsComponent";
import DynamicFormComponent from "./DynamicFormComponent";
import { buildUserData, DYNAMIC_FRAGMENT } from "../../utils/dynamicForm";
import { serviceTypeEnum } from "./ServiceTypeEnum";
import { ConfigLinksDetailsContext } from "../../Context/cofingLinksDetailsContext";
import { useMemo } from "react";
import { filePathToFile } from "../../utils/pathToFileObject";
import VerificationCodeField from "../VerificationCodeField";
import AttachmentUploadRows from "../AttachmentUploadRows";
import { useAttachmentRules } from "../../hooks/useAttachmentRules";

const RequestServiceForm = ({ isGuestMode = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { state } = location;
  const { userDetails } = useContext(UserDetailsContext);
  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const [categories, setCategories] = useState([]);
  const [externalEntities, setExternalEntities] = useState([]);
  const [propertyUsages, setPropertyUsages] = useState([]);
  const [currentTypes, setCurrentTypes] = useState([]);
  const [craftsAndIndustries, setCraftsAndIndustries] = useState([]);
  const [beneficiaryTypes, setBeneficiaryTypes] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [quarters, setQuarters] = useState([]);
  const [parcels, setParcels] = useState([]);
  const [showFromMap, setShowFromMap] = useState(false);
  const [services, setServices] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [showFile, setShowFile] = useState(false);
  const [showAppliedBy, setShowAppliedBy] = useState(false);
  const [fileToPreviewData, setFileToPreviewData] = useState();
  const [openSubmissionModal, setOpenSubmissionModal] = useState(false);
  const [openFailiurModal, setOpenFailureModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [attachmentLoading, setAttachmentLoading] = useState(false);
  const [delegations, setDelegations] = useState([]);
  const [selectedAccountFor, setSelectedAccountFor] = useState('self');
  const [challengeId, setChallengeId] = useState('');
  const [challengeCode, setChallengeCode] = useState('');
  // Definition of the FormBuilder form, set by DynamicFormComponent once it has loaded.
  const [dynamicForm, setDynamicForm] = useState(null);
  const verificationCodeRef = useRef(null);
  const {
    handleSubmit,
    control,
    setValue,
    getValues,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onChange",
  });

  // Guard: if navigated here without state (e.g., direct URL access), redirect home
  useEffect(() => {
    if (!state) navigate("/", { replace: true });
  }, []);
  if (!state) return null;

  const isGeneralForm =
    state.fragment === "General" || state.fragment === "GeneralCategories";
  const withCategories = state.fragment === "GeneralCategories";
  const isWithCertificateForm =
    state.fragment === "Certificate" || state.isWithCertificate;
  const isElectricityForm = state.fragment === "Electricity";
  const isWaterForm = state.fragment === "Water";
  const isCraftmentForm = state.fragment === "NewCrafts";
  const isServiceWithDetailsForm = state.fragment === "ServiceWithDetails";
  const isDynamicForm = state.fragment === DYNAMIC_FRAGMENT && !!state.formId;
  const isFromMapForm =
    state.serviceSelectType === serviceTypeEnum.showFromMapAndServiceTypeForm ||
    state.serviceSelectType === serviceTypeEnum.showFromMapForm;
  const isServiceTypeForm =
    state.serviceSelectType === serviceTypeEnum.showFromMapAndServiceTypeForm ||
    state.serviceSelectType === serviceTypeEnum.showServiceTypeForm;
  const isClearanceCertificateForm = state.fragment === "ClearanceCertificate";
  const { validFileTypes, fileError } = useAttachmentRules();

  const selfDisplayInfo = useMemo(() => {
    const isCitizen = userDetails.type === 3 || !userDetails.type;
    const isCompany = userDetails.type === 1;
    return {
      isCompany,
      fullName: isCitizen ? userDetails.citizen?.fullName : userDetails.company?.companyName,
      docLabel: isCompany ? 'رقم التسجيل' : 'رقم الوثيقة',
      docTypeLabel: isCitizen ? userDetails.citizen?.documentType?.textA : null,
      docNumber: isCitizen ? userDetails.citizen?.documentNumber : userDetails.company?.businessLicenseNo,
      phone: isCitizen
        ? (userDetails.citizen?.phone1 || userDetails.citizen?.telephone)
        : (userDetails.company?.telephone1 || userDetails.company?.telephone2),
      email: isCitizen ? userDetails.citizen?.email : userDetails.company?.email,
      address: isCitizen ? userDetails.citizen?.address : userDetails.company?.address,
    };
  }, [userDetails]);

  const accountForDisplayInfo = useMemo(() => {
    if (selectedAccountFor === 'self') return selfDisplayInfo;
    const delegate = delegations.find(d => d.delegateId === selectedAccountFor);
    const isCompanyDelegate = delegate?.type === 1;
    return {
      isCompany: isCompanyDelegate,
      fullName: delegate?.fullName || '',
      docLabel: isCompanyDelegate ? 'رقم التسجيل' : 'رقم الوثيقة',
      docNumber: delegate?.delegateNum || '',
      phone: '',
      email: '',
    };
  }, [selectedAccountFor, delegations, selfDisplayInfo]);

  const accountForUserId = selectedAccountFor === 'self' ? userDetails.userId : selectedAccountFor;

  useEffect(() => {
    const getData = async () => {
      try {
        setIsLoadingData(true);

        const fetchCategories = () =>
          axiosInstance.get("/lookup/category").then(setCategories);
        const fetchExternalEntities = () =>
          axiosInstance.get("/lookup/ExternalEntities").then(setExternalEntities);
        const fetchPropertyUsages = () =>
          axiosInstance.get("/lookup/UseProperties").then(setPropertyUsages);
        const fetchElectricityVoltages = () =>
          axiosInstance.get("/lookup/ElectricityVoltages").then(setCurrentTypes);
        const fetchCraftsAndIndustries = () =>
          axiosInstance.get("/lookup/CraftsAndIndustries").then(setCraftsAndIndustries);
        const fetchServices = () =>
          axiosInstance.get(`/User/${userDetails.userId}/Services`).then(setServices);
        const fetchMapData = () =>
          Promise.all([
            axiosInstance.get("/lookup/BeneficiaryTypes"),
            axiosInstance.get("/lookup/Blocks"),
            axiosInstance.get("/lookup/Quarter"),
            axiosInstance.get("/lookup/Parcels"),
          ]).then(([beneficiaryTypesData, blocksData, quartersData, parcelsData]) => {
            setBeneficiaryTypes(beneficiaryTypesData);
            setBlocks(blocksData);
            setQuarters(quartersData);
            setParcels(parcelsData);
          });

        const promises = [];
        if (withCategories) promises.push(fetchCategories());
        if (isWithCertificateForm || isClearanceCertificateForm)
          promises.push(fetchExternalEntities());
        if (isElectricityForm) {
          promises.push(fetchPropertyUsages());
          promises.push(fetchElectricityVoltages());
        }
        if (isWaterForm) promises.push(fetchPropertyUsages());
        if (isCraftmentForm) promises.push(fetchCraftsAndIndustries());
        if (isFromMapForm) promises.push(fetchMapData());
        if (!isGuestMode && (isServiceTypeForm || isClearanceCertificateForm))
          promises.push(fetchServices());

        await Promise.all(promises);

        if (!isGuestMode) {
          try {
            const delegationsData = await axiosInstance.get('/User/Delegations');
            setDelegations(Array.isArray(delegationsData) ? delegationsData : []);
          } catch (e) {
            setDelegations([]);
          }
        }

        setIsLoadingData(false);
      } catch (error) {
        console.log("Error loading data", error);
      }
    };
    const getAttachments = async () => {
      try {
        setAttachmentLoading(true);
        if (isGuestMode) {
          setAttachmentLoading(false);
          return;
        }

        const allUserAttachments = await axiosInstance.get(`/user/attachments`);
        setAttachmentLoading(false);
        state.requiredAttachments.forEach(async (neededAttachment, index) => {
          const alreadyExistingAttachment = allUserAttachments.find(
            (item) =>
              item.attachmentType.id === neededAttachment.attachmentTypeId
          );
          if (alreadyExistingAttachment) {
            const fileObject = await filePathToFile(
              alreadyExistingAttachment.attachmentPath,
              alreadyExistingAttachment.attachmentName
            );
            setValue(`attachment${index}`, fileObject);
          }
        });
      } catch (e) {
        console.log("e", e);
      }
    };
    getData();
    getAttachments();
  }, []);

  useEffect(() => {
    if (state.serviceSelectType === serviceTypeEnum.showFromMapForm) {
      setShowFromMap(true);
    }
  }, [state.serviceSelectType]);

  const buildDetails = ({ data }) => {
    let stringifiedData;
    switch (true) {
      case state.fragment === "General":
        stringifiedData = [{ Notes: data?.details }];
        break;
      case state.fragment === "GeneralCategories":
        stringifiedData = [
          { Notes: data?.details, GeneralCategories: data?.category },
        ];
        break;
      case state.fragment === "Certificate":
        stringifiedData = {
          ExternalEntities: externalEntities.find(
            (item) => item.id == data?.externalEntities
          )?.name,
        };
        break;
      case state.fragment === "Electricity":
        stringifiedData = [
          {
            UseProperty: data?.propertyUsage,
            PropertyDescription: data?.propertyDescription,
            ElectricityVoltage: data?.currentType,
          },
        ];
        break;
      case state.fragment === "Water":
        stringifiedData = [
          {
            UseProperty: data?.propertyUsage,
            PropertyDescription: data?.propertyDescription,
          },
        ];
        break;
      case state.fragment === "NewCrafts":
        const selectedCraft = getValues("craftsAndIndustries");
        stringifiedData = [
          {
            CraftsType: selectedCraft.id,
            CraftPrice: selectedCraft.price,
            RenterName: data.renterName,
            RenterDocument: data?.renterDocument,
            TradeName: data?.tradeName,
            BusinessLicenseNo: data?.businessLicenseNumber,
            CraftsNotes: data?.craftNotes,
          },
        ];
        break;
      case state.fragment === "ServiceWithDetails":
        stringifiedData = [
          {
            Notes: data?.serviceDetails,
            AdditionalData: data?.serviceTitle,
          },
        ];
        break;
      case state.fragment === DYNAMIC_FRAGMENT:
        // Same shape the staff app sends: { FormName, AdditionalData: <formRender userData as text> }
        stringifiedData = {
          FormName: dynamicForm?.formName,
          AdditionalData: JSON.stringify(
            buildUserData(dynamicForm?.fields, data?.dynamic)
          ),
        };
        break;
      case state.fragment === "ClearanceCertificate":
        const selectedService = services.map(
          (service) => service.subscriberNumber
        );
        stringifiedData = {
          servicesList: JSON.stringify(selectedService),
          ExternalEntities: externalEntities.find(
            (item) => item.id == data?.externalEntities
          )?.name,
        };
        break;
    }
    if (state.isWithCertificate) {
      return JSON.stringify([
        stringifiedData,
        {
          ExternalEntities: externalEntities.find(
            (item) => item.id == data?.externalEntities
          )?.name,
        },
      ]);
    }
    return JSON.stringify(stringifiedData);
  };

  const buildMapData = ({ data }) => {
    var jsonArr = [];
    const dataObject = {
      BlockCode: data?.block?.code,
      BlockNum: data?.block?.code,
      BlockNameA: data?.block?.name,
      QuarterCode: data?.quarter?.code,
      QuarterNameA: data?.quarter?.name,
      ParcelNumber: data?.parcel?.code,
    };
    jsonArr.push(dataObject);

    return JSON.stringify(jsonArr);
  };

  const onViewDocumentClick = (file) => {
    setFileToPreviewData({ fileObject: file });
    setShowFile(true);
  };

  const handleCloseFilePreview = () => {
    setShowFile(false);
    setFileToPreviewData();
  };

  const buildGuestServiceGoal = (data) => {
    const baseGoal = (data?.serviceGoal || "").trim();
    const contactName = (data?.guestContactName || "").trim();
    const contactPhone = (data?.guestContactPhone || "").trim();

    if (!contactName && !contactPhone) {
      return baseGoal;
    }

    const contactLines = [];
    if (contactName) contactLines.push(`الاسم: ${contactName}`);
    if (contactPhone) contactLines.push(`الهاتف: ${contactPhone}`);

    return `${baseGoal}\n\n--- بيانات التواصل ---\n${contactLines.join("\n")}`;
  };

  const onSubmit = async (data) => {
    try {
      if (isDynamicForm && !dynamicForm) {
        setOpenFailureModal(true);
        return;
      }
      setIsLoading(true);
      const combinedServiceData = buildDetails({
        details: data?.details,
        selectedCategory: data?.category,
        data,
      });
      const formData = new FormData();
      let attachmentIndex = 0;
      if (!isGuestMode) {
        state.requiredAttachments.forEach((item, index) => {
          if (
            data[`attachment${index}`]?.name ||
            data[`attachment${index}`]?.type ||
            data[`attachment${index}`]?.file
          ) {
            formData.append(
              `attachments[${attachmentIndex}].File`,
              data[`attachment${index}`]
            );
            formData.append(
              `attachments[${attachmentIndex}].AttachmentType`,
              item?.attachmentTypeId
            );
            attachmentIndex++;
          }
        });

        const dataRequestObj = {
          applicantId: userDetails.userId,
          subscriberNumber: data?.selectedService ? `${data?.selectedService}` : "",
          accountFor: accountForUserId,
          isFromMap: showFromMap,
          mapData: showFromMap ? buildMapData({ data }) : "",
          beneficiaryType: data?.beneficiaryType ? `${data?.beneficiaryType}` : "",
          publicService: state.serviceId,
          serviceGoal: data.serviceGoal,
          serviceReviewNote: "",
          appliedBy: data.appliedBy,
          appliedByDocumentNumber: data.appliedByDocumentNumber,
          appliedByRelation: data.appliedByRelation,
          serviceData: combinedServiceData || null,
        };

        const applicationResponse = await axiosInstance.post("/application", dataRequestObj);
        const subscriberServiceId = applicationResponse.subscriberServiceId;
        await axiosInstance.post(`/application/${subscriberServiceId}/attachments`, formData);

        setIsLoading(false);
        setOpenSubmissionModal(true);
      } else {
        const guestServiceGoal = buildGuestServiceGoal(data);
        const guestRequestObj = {
          PublicService: state.serviceId,
          ServiceGoal: guestServiceGoal,
          ServiceData: combinedServiceData || "[]",
          ServiceReviewNote: "",
          SubscriberNumber: data?.selectedService ? `${data?.selectedService}` : "",
          MapData: showFromMap ? buildMapData({ data }) : "",
          FlatsData: "[]",
          IsFromMap: showFromMap,
          ChallengeId: challengeId,
          ChallengeCode: challengeCode,
        };
        const guestResponse = await axiosInstance.post("/Guest/Application", guestRequestObj);
        const guestSubscriberServiceId = guestResponse?.subscriberServiceId;

        // Upload guest attachments if any
        if (guestSubscriberServiceId && state.requiredAttachments?.length) {
          const attFormData = new FormData();
          let attIdx = 0;
          state.requiredAttachments.forEach((item, index) => {
            const file = data[`attachment${index}`];
            if (file?.name || file?.type || file?.file) {
              attFormData.append(`attachments[${attIdx}].File`, file);
              attFormData.append(`attachments[${attIdx}].AttachmentType`, item.attachmentTypeId);
              attIdx++;
            }
          });
          if (attIdx > 0) {
            try {
              await axiosInstance.post(`/Guest/Application/${guestSubscriberServiceId}/Attachments`, attFormData);
            } catch (e) {
              console.log('Guest attachments upload failed (non-critical)', e);
            }
          }
        }

        setIsLoading(false);
        navigate('/guest/order-details', {
          state: {
            result: {
              subscriberServiceId: guestSubscriberServiceId,
              status: 1,
              serviceName: state.name,
              requestNumber: '',
              insertDate: new Date().toISOString(),
              applicantName: '',
            }
          }
        });
        return;
      }
    } catch (e) {
      console.log("e", e);
      setIsLoading(false);
      if (isGuestMode) {
        verificationCodeRef.current?.refresh();
      } else {
        setOpenFailureModal(true);
      }
    }
  };

  const onImageUpload = (field, file, index) => {
    // User cancelled the file dialog — do nothing
    if (!file) return;

    const message = fileError(file);
    if (message) {
      setError(`attachment${index}`, { type: "validate", message });
      return;
    }
    field.onChange(file);
    // Clear any previous error message
    clearErrors(`attachment${index}`);
  };

  const onSuccessButtonClick = () => {
    if (isGuestMode) {
      navigate("/guest-track-request");
      return;
    }
    navigate("/home");
  };

  const handleShowServiceTitleForms = (ServiceSelectType) => {
    if (isGuestMode && (isServiceTypeForm || isClearanceCertificateForm)) {
      return <></>;
    }

    switch (ServiceSelectType) {
      case serviceTypeEnum.showFromMapAndServiceTypeForm:
        return (
          <>
            <ChooseLocationFromMapForm
              control={control}
              errors={errors}
              setValue={setValue}
              isServiceRequired={state.isServiceRequired}
              beneficiaryTypes={beneficiaryTypes}
              blocks={blocks}
              parcels={parcels}
              quarters={quarters}
              setShowFromMap={setShowFromMap}
              showFromMap={showFromMap}
              bothFormsPresented={true}
            />
            <ServiceTypeForm
              isServiceRequired={state.isServiceRequired}
              control={control}
              errors={errors}
              setValue={setValue}
              services={services}
              showFromMap={showFromMap}
            />
          </>
        );

      case serviceTypeEnum.showFromMapForm:
        return (
          <ChooseLocationFromMapForm
            control={control}
            errors={errors}
            setValue={setValue}
            isServiceRequired={state.isServiceRequired}
            beneficiaryTypes={beneficiaryTypes}
            blocks={blocks}
            parcels={parcels}
            quarters={quarters}
          />
        );
      case serviceTypeEnum.showServiceTypeForm:
        return (
          <ServiceTypeForm
            isServiceRequired={state.isServiceRequired}
            control={control}
            errors={errors}
            setValue={setValue}
            services={services}
          />
        );
      case serviceTypeEnum.showNone:
        return <></>;
      default:
        return <></>;
    }
  };

  if (isLoadingData) {
    return (
      <Box sx={{ textAlign: "center" }}>
        <CircularProgress size={80} />
      </Box>
    );
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack gap={2}>
          {!isGuestMode && (
            <Paper elevation={2} sx={{ p: 3, borderRadius: "12px", border: "1px solid #e0e0e0" }}>
              <Grid container spacing={2} alignItems="flex-start">
                {/* Applicant info */}
                <Grid item xs={12} sm={6} md={4}>
                  <Box display="flex" alignItems="center" gap={1.5}>
                    <PersonIcon sx={{ color: "primary.main", fontSize: 22 }} />
                    <Stack direction="row" gap={1} flexWrap="wrap">
                      <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>اسم مقدم الطلب:</Typography>
                      <Typography sx={{ color: "text.secondary" }}>{selfDisplayInfo.fullName}</Typography>
                    </Stack>
                  </Box>
                </Grid>
                {selfDisplayInfo.docTypeLabel && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <DescriptionIcon sx={{ color: "primary.main", fontSize: 22 }} />
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>نوع الوثيقة:</Typography>
                        <Typography sx={{ color: "text.secondary" }}>{selfDisplayInfo.docTypeLabel}</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                {selfDisplayInfo.docNumber && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <BadgeIcon sx={{ color: "primary.main", fontSize: 22 }} />
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>{selfDisplayInfo.docLabel}:</Typography>
                        <Typography sx={{ color: "text.secondary" }}>{selfDisplayInfo.docNumber}</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                {selfDisplayInfo.phone && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <PhoneIcon sx={{ color: "primary.main", fontSize: 22 }} />
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>رقم الموبايل:</Typography>
                        <Typography sx={{ color: "text.secondary" }}>{selfDisplayInfo.phone}</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                {selfDisplayInfo.email && (
                  <Grid item xs={12} sm={6} md={6}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <EmailIcon sx={{ color: "primary.main", fontSize: 22 }} />
                      <Stack direction="row" gap={1} flexWrap="wrap" sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>البريد الإلكتروني:</Typography>
                        <Typography title={selfDisplayInfo.email} sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", color: "text.secondary", flex: 1, minWidth: 0 }}>{selfDisplayInfo.email}</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                {selfDisplayInfo.address && (
                  <Grid item xs={12} sm={6} md={6}>
                    <Box display="flex" alignItems="center" gap={1.5}>
                      <HomeIcon sx={{ color: "primary.main", fontSize: 22 }} />
                      <Stack direction="row" gap={1} flexWrap="wrap">
                        <Typography sx={{ fontWeight: "bold", color: "text.primary" }}>العنوان:</Typography>
                        <Typography sx={{ color: "text.secondary" }}>{selfDisplayInfo.address}</Typography>
                      </Stack>
                    </Box>
                  </Grid>
                )}
                {/* حساب عن section — only shown when the user has delegations */}
                {delegations.length > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Divider textAlign="left">
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold", color: "primary.main" }}>حساب عن</Typography>
                      </Divider>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography sx={{ fontWeight: "bold", mb: 1 }}>اختر الجهة</Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={selectedAccountFor}
                        onChange={(e) => setSelectedAccountFor(e.target.value)}
                        variant="outlined"
                      >
                        <MenuItem value="self">
                          {selfDisplayInfo.fullName || 'أنت'} (أنت)
                        </MenuItem>
                        {delegations.map((d) => (
                          <MenuItem key={d.delegateId} value={d.delegateId}>
                            {d.fullName} - {d.delegateNum} {d.type === 1 ? '(شركة)' : ''}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    {selectedAccountFor !== 'self' && accountForDisplayInfo.fullName && (
                      <>
                        <Grid item xs={12} md={4}>
                          <Typography sx={{ mb: 1, visibility: 'hidden', display: { xs: 'none', md: 'block' } }}>spacer</Typography>
                          <Box display="flex" alignItems="center" gap={1} height="40px">
                            <PersonIcon sx={{ color: "primary.main", fontSize: 20 }} />
                            <Typography sx={{ fontWeight: "bold" }}>الاسم:</Typography>
                            <Typography sx={{ color: "text.secondary" }}>{accountForDisplayInfo.fullName}</Typography>
                          </Box>
                        </Grid>
                        {accountForDisplayInfo.docNumber && (
                          <Grid item xs={12} md={4}>
                            <Typography sx={{ mb: 1, visibility: 'hidden', display: { xs: 'none', md: 'block' } }}>spacer</Typography>
                            <Box display="flex" alignItems="center" gap={1} height="40px">
                              <BadgeIcon sx={{ color: "primary.main", fontSize: 20 }} />
                              <Typography sx={{ fontWeight: "bold" }}>{accountForDisplayInfo.docLabel}:</Typography>
                              <Typography sx={{ color: "text.secondary" }}>{accountForDisplayInfo.docNumber}</Typography>
                            </Box>
                          </Grid>
                        )}
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Paper>
          )}

          {!isGuestMode && <Divider textAlign="left" sx={{ mb: 2 }}>
            <Stack
              direction={"row"}
              justifyContent={"center"}
              alignItems={"center"}
              sx={{
                px: 2,
              }}
            >
              <Typography sx={{ fontWeight: "bold", fontSize: "1.1rem", color: "primary.main" }}>قدم بواسطة</Typography>
              <IconButton onClick={() => setShowAppliedBy((prev) => !prev)} sx={{ color: "primary.main" }}>
                <ExpandCircleDownIcon
                  sx={{
                    transform: showAppliedBy
                      ? "rotate(180deg)"
                      : "rotate(0deg)",
                    transition: "transform 0.3s ease",
                  }}
                />
              </IconButton>
            </Stack>
          </Divider>}
          {!isGuestMode && showAppliedBy && (
            <>
              <Paper
                elevation={1}
                sx={{ 
                  p: 3, 
                  borderRadius: "12px",
                  border: "1px solid #e0e0e0"
                }}
              >
                <Grid spacing={2} container>
                  <Grid item md={4} xs={12}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      قدم بواسطة
                    </Typography>
                    <Controller
                      name="appliedBy"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            placeholder: "",
                          }}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <Typography sx={{ fontWeight: "bold" }}>
                      رقم الوثيقة
                    </Typography>
                    <Controller
                      name="appliedByDocumentNumber"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          inputProps={{
                            style: {},
                          }}
                          InputProps={{
                            placeholder: "",
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item md={4} xs={12}>
                    <Typography sx={{ fontWeight: "bold" }}>الصفة</Typography>
                    <Controller
                      name="appliedByRelation"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          variant="outlined"
                          fullWidth
                          InputProps={{
                            placeholder: "",
                          }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Paper>
              <Divider />
            </>
          )}

          <Box
            sx={{
              background: "#fff",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 1.5,
              px: 3,
              width: isMobile ? "100%" : 300,
              borderRadius: "8px",
              border: "2px solid",
              borderColor: "primary.main",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
              وصف الطلب
            </Typography>
          </Box>

          <Paper
            elevation={1}
            sx={{ 
              p: 3, 
              borderRadius: "12px",
              border: "1px solid #e0e0e0"
            }}
          >
            <Grid container>
              <Grid item xs={12} md={12}>
                <Typography sx={{ fontWeight: "bold" }}>
                  الغاية من تقديم الطلب *
                </Typography>
                <Controller
                  name="serviceGoal"
                  control={control}
                  defaultValue=""
                  rules={{
                    required: "يجب ادخال الغاية من تقديم الطلب",
                  }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      multiline
                      minRows={1}
                      maxRows={8}
                      variant="outlined"
                      FormHelperTextProps={{
                        sx: {
                          textAlign: "start",
                        },
                      }}
                      error={!!errors.serviceGoal}
                      helperText={
                        errors.serviceGoal ? errors.serviceGoal.message : ""
                      }
                    />
                  )}
                />
              </Grid>
            </Grid>
          </Paper>

          {isGuestMode && (
            <Paper
              elevation={1}
              sx={{
                p: 3,
                borderRadius: "12px",
                border: "1px solid #e0e0e0",
                mt: 2,
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography sx={{ fontWeight: "bold" }}>
                    بيانات التواصل (اختياري)
                  </Typography>
              
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontWeight: "bold" }}>الاسم</Typography>
                  <Controller
                    name="guestContactName"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        placeholder="الاسم للتواصل"
                      />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography sx={{ fontWeight: "bold" }}>رقم الهاتف</Typography>
                  <Controller
                    name="guestContactPhone"
                    control={control}
                    defaultValue=""
                    rules={{
                      pattern: {
                        value: /^[0-9+\-\s()]{7,20}$/,
                        message: "صيغة رقم الهاتف غير صحيحة",
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        placeholder="مثال: 079xxxxxxx"
                        error={!!errors.guestContactPhone}
                        helperText={errors.guestContactPhone?.message || ""}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          )}

          {isGeneralForm && (
            <GeneralComponent
              control={control}
              categories={categories}
              errors={errors}
              withCategories={withCategories}
            />
          )}
          {isWithCertificateForm && (
            <CertificateComponent
              externalEntities={externalEntities}
              control={control}
              errors={errors}
            />
          )}
          {isElectricityForm && (
            <ElectricityComponent
              currentTypes={currentTypes}
              propertyUsage={propertyUsages}
              control={control}
              errors={errors}
            />
          )}
          {isWaterForm && (
            <WaterComponent
              propertyUsage={propertyUsages}
              control={control}
              errors={errors}
            />
          )}
          {isCraftmentForm && (
            <CraftmentForm
              control={control}
              errors={errors}
              craftsAndIndustries={craftsAndIndustries}
            />
          )}
          {isDynamicForm && (
            <DynamicFormComponent
              formId={state.formId}
              control={control}
              errors={errors}
              onLoaded={setDynamicForm}
            />
          )}
          {isServiceWithDetailsForm && (
            <ServiceWithDetailsComponent
              control={control}
              errors={errors}
            />
          )}
          {isClearanceCertificateForm && (
            <ClearanceCertificateForm
              control={control}
              errors={errors}
              setValue={setValue}
              services={services}
              externalEntities={externalEntities}
            />
          )}
          {handleShowServiceTitleForms(state.serviceSelectType)}
          {state.requiredAttachments.length ? (
            attachmentLoading ? (
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress size={80} />
              </Box>
            ) : (
              <>
                <Box
                  sx={{
                    background: "#fff",
                    fontWeight: "bold",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 1.5,
                    px: 3,
                    width: isMobile ? "100%" : 300,
                    borderRadius: "8px",
                    border: "2px solid",
                    borderColor: "primary.main",
                  }}
                >
                  <Typography variant="h6" sx={{ fontWeight: "bold", color: "primary.main" }}>
                    نوع المرفق
                  </Typography>
                </Box>
                <Paper
                  elevation={1}
                  sx={{ 
                    p: 3, 
                    borderRadius: "12px",
                    border: "1px solid #e0e0e0"
                  }}
                >
                  <AttachmentUploadRows
                    items={state.requiredAttachments}
                    control={control}
                    errors={errors}
                    accept={validFileTypes}
                    onPick={onImageUpload}
                    onView={onViewDocumentClick}
                  />
                </Paper>
              </>
            )
          ) : null}

          {isGuestMode && (
            <VerificationCodeField
              ref={verificationCodeRef}
              challengePath="/Guest/ApplicationChallenge"
              value={challengeCode}
              onValueChange={setChallengeCode}
              onChallengeIdChange={setChallengeId}
              disabled={isLoading}
            />
          )}

          <LoadingButton
            type="submit"
            sx={{ 
              width: isMobile ? "80%" : "30%", 
              alignSelf: "center",
              py: 1.5,
              borderRadius: "12px",
              fontSize: "1.1rem",
              fontWeight: "bold",
              boxShadow: "0px 4px 12px rgba(25, 118, 210, 0.3)",
              "&:hover": {
                boxShadow: "0px 6px 16px rgba(25, 118, 210, 0.4)",
                transform: "translateY(-2px)",
              },
              transition: "all 0.3s ease",
            }}
            variant="contained"
            loading={isLoading}
          >
            <Typography sx={{ fontSize: "1.1rem", fontWeight: "bold" }}>تقديم الطلب</Typography>
          </LoadingButton>
          {Object.keys(errors).length ? (
            <Typography color="error">
              يرجى التاكد من تعبئة جميع الحقول المطلوبة
            </Typography>
          ) : null}
        </Stack>

        {fileToPreviewData && (
          <PreviewAttachmentDialog
            open={showFile}
            attachmentDetails={fileToPreviewData}
            handleClose={handleCloseFilePreview}
          />
        )}
      </form>

      <MessageModal
        openModal={openFailiurModal}
        message={
          "لقد حدث خطا اثناء عملية طلب الخدمة, يرجى المحاولة مرة اخرى فيما بعد"
        }
        buttonText={"اغلاق"}
        interfaceIcon={
          <CancelIcon color={"primary"} sx={{ height: 80, width: 80 }} />
        }
        onCloseModal={() => setOpenFailureModal(false)}
      />

      <Dialog
        open={openSubmissionModal}
        maxWidth="md"
        fullWidth
        sx={{
          "& .MuiDialog-paper": {
            border: "1px solid #BC1F41",
            borderRadius: 3,
            padding: 3,
          },
        }}
      >
        <Stack justifyContent={"center"} alignItems={"center"}>
          <CheckCircleIcon color={"primary"} sx={{ height: 80, width: 80 }} />
          <Typography sx={{ fontWeight: "bold" }} variant="h6">
            تم تقديم طلبكم بنجاح
          </Typography>
          <Typography sx={{ fontWeight: "bold" }} variant="h6">
            سوف يتم معالجة طلبكم في اقرب وقت ممكن
          </Typography>
          <Button
            type="submit"
            sx={{ width: "30%", alignSelf: "center", marginTop: 4 }}
            variant="contained"
            onClick={onSuccessButtonClick}
          >
            <Typography>{isGuestMode ? "العودة إلى بوابة الزوار" : "العودة الي لوحة البيانات"}</Typography>
          </Button>
        </Stack>
      </Dialog>
    </>
  );
};

export default RequestServiceForm;
